/**
 * One linked-resource row, in whichever of its four shapes applies: an internal
 * route, an external link, inert, or disabled.
 *
 * The shape is chosen here rather than by the caller so every row in the card
 * is described by what it points at, not by which element to render.
 */
import { ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

import ReviewLinkBody, { type ReviewLinkBodyProps } from './ReviewLinkBody';

export interface ReviewLinkRowProps extends ReviewLinkBodyProps {
  /** Reads normally but goes nowhere — there is no route for it (the customer
   *  record has no detail page yet). Distinct from `disabled`, which greys the
   *  row to say the resource does not exist. */
  info?: boolean;
  /** Internal route; mutually exclusive with `href`. */
  to?: string;
  href?: string;
}

const ROW_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: 11,
  width: '100%',
  padding: '10px 14px',
  border: 0,
  background: 'transparent',
  textDecoration: 'none',
  borderBottom: '1px solid var(--nb-border)',
  textAlign: 'left',
} as const;

export default function ReviewLinkRow({ to, href, info, ...body }: ReviewLinkRowProps) {
  if (body.disabled || info) {
    return (
      <div style={ROW_STYLE}>
        <ReviewLinkBody {...body} />
      </div>
    );
  }
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={ROW_STYLE}>
        <ReviewLinkBody {...body} />
        <ExternalLink size={13} style={{ color: 'var(--nb-faint)', flex: '0 0 13px' }} />
      </a>
    );
  }
  if (to) {
    return (
      <Link to={to} style={ROW_STYLE}>
        <ReviewLinkBody {...body} />
        <ChevronRight size={14} style={{ color: 'var(--nb-faint)', flex: '0 0 14px' }} />
      </Link>
    );
  }
  return (
    <div style={ROW_STYLE}>
      <ReviewLinkBody {...body} />
    </div>
  );
}
